<script setup lang="ts">
import PublicPageLayout from "~/components/public/PublicPageLayout.vue";

const { t, locale } = useI18n();
const localeRoute = useLocaleRoute();

const operatorName = "Anton Rubchanka";
const operatorEmail = "info@senta.lt";

// TODO: Add a real correspondence address
// Example: "Street 1-2, LT-00000 Vilnius, Lithuania"
const correspondenceAddress = "";

const updatedAt = computed(() =>
  new Intl.DateTimeFormat(locale.value, {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(new Date("2026-08-18T00:00:00Z")),
);

const sections = [
  {
    key: "general",
    blocks: [
      { paragraph: "p1" },
      { paragraph: "p2" },
      { paragraph: "p3" },
      { paragraph: "p4" },
    ],
  },
  {
    key: "controller",
    blocks: [{ paragraph: "p1" }, { paragraph: "p2" }],
  },
  {
    key: "sources",
    blocks: [
      {
        paragraph: "p1",
        children: [
          "direct",
          "automatic",
          "google",
          "facebook",
          "providers",
          "other_people",
        ],
      },
      { paragraph: "p2" },
    ],
  },
  {
    key: "account",
    blocks: [
      {
        paragraph: "p1",
        children: [
          "first_name",
          "last_name",
          "email",
          "password",
          "role",
          "verification",
          "language",
          "profile_image",
          "timestamps",
          "technical",
        ],
      },
      { paragraph: "p2" },
      { paragraph: "p3" },
      { paragraph: "p4" },
    ],
  },
  {
    key: "social_login",
    blocks: [
      {
        paragraph: "google",
        children: [
          "google_id",
          "google_email",
          "google_name",
          "google_photo",
          "google_auth",
        ],
      },
      { paragraph: "google_scope" },
      {
        paragraph: "facebook",
        children: [
          "facebook_id",
          "facebook_name",
          "facebook_photo",
          "facebook_email",
        ],
      },
      { paragraph: "facebook_scope" },
      { paragraph: "basis" },
      { paragraph: "retention" },
    ],
  },
  {
    key: "professional_profile",
    blocks: [
      {
        paragraph: "p1",
        children: [
          "name",
          "photo",
          "contacts",
          "category",
          "services",
          "prices",
          "regions",
          "languages",
          "experience",
          "education",
          "overview",
          "portfolio",
          "reviews",
          "other",
        ],
      },
      { paragraph: "p2" },
      { paragraph: "p3" },
      { paragraph: "p4" },
    ],
  },
  {
    key: "public_profile",
    blocks: [
      { paragraph: "p1" },
      {
        paragraph: "p2",
        children: [
          "name",
          "photo",
          "description",
          "services",
          "prices",
          "regions",
          "languages",
          "experience",
          "portfolio",
          "reviews",
          "phone",
          "email",
        ],
      },
      { paragraph: "p3" },
      { paragraph: "p4" },
    ],
  },
  {
    key: "search_ranking",
    blocks: [
      {
        paragraph: "p1",
        children: [
          "category",
          "service",
          "location",
          "language",
          "experience",
          "price",
          "rating",
          "profile_quality",
          "freshness",
          "sorting",
        ],
      },
      { paragraph: "p2" },
      { paragraph: "p3" },
    ],
  },
  {
    key: "reviews",
    blocks: [
      {
        paragraph: "p1",
        children: [
          "author",
          "professional",
          "service",
          "rating",
          "text",
          "photos",
          "date",
          "moderation",
        ],
      },
      { paragraph: "p2" },
      { paragraph: "p3" },
      { paragraph: "p4" },
      { paragraph: "p5" },
    ],
  },
  {
    key: "moderation_support",
    blocks: [
      {
        paragraph: "p1",
        children: [
          "reporter",
          "content",
          "complaint",
          "evidence",
          "explanations",
          "history",
          "decision",
          "signals",
        ],
      },
      { paragraph: "p2" },
      { paragraph: "p3" },
      {
        paragraph: "p4",
        children: [
          "support_name",
          "support_email",
          "support_phone",
          "support_message",
        ],
      },
      { paragraph: "p5" },
      { paragraph: "p6" },
    ],
  },
  {
    key: "security_email",
    blocks: [
      {
        paragraph: "security",
        children: [
          "ip",
          "time",
          "device",
          "request",
          "login",
          "events",
          "logs",
        ],
      },
      { paragraph: "security_basis" },
      {
        paragraph: "security_retention",
        children: ["nginx", "application", "incident"],
      },
      { paragraph: "email" },
      {
        paragraph: "email_data",
        children: [
          "recipient",
          "type",
          "subject",
          "status",
          "provider_id",
          "error",
          "content",
        ],
      },
      {
        paragraph: "email_retention",
        children: ["email_logs", "email_body"],
      },
    ],
  },
  {
    key: "analytics_cookies",
    blocks: [
      {
        paragraph: "analytics",
        children: [
          "pages",
          "sessions",
          "interactions",
          "navigation",
          "referrer",
          "browser",
          "device",
          "location",
          "identifiers",
        ],
      },
      { paragraph: "no_pii" },
      { paragraph: "consent" },
      { paragraph: "analytics_retention" },
      { paragraph: "necessary_cookies" },
      {
        paragraph: "analytics_cookies",
        children: ["ga", "ga_container"],
      },
      { paragraph: "cookie_lifetime" },
    ],
    cookiesLink: true,
  },
  {
    key: "recipients",
    blocks: [
      {
        paragraph: "p1",
        children: [
          "hostinger",
          "resend",
          "google",
          "meta",
          "advisers",
          "authorities",
        ],
      },
      { paragraph: "p2" },
    ],
  },
  {
    key: "international",
    blocks: [
      { paragraph: "p1" },
      { paragraph: "p2" },
      { paragraph: "p3" },
      {
        paragraph: "p4",
        children: ["adequacy", "scc", "other"],
      },
      { paragraph: "p5" },
    ],
  },
  {
    key: "retention_deletion",
    blocks: [
      {
        paragraph: "account",
        children: ["account_inactive", "account_90", "account_delete"],
      },
      { paragraph: "professional" },
      { paragraph: "professional_90" },
      { paragraph: "backups" },
      { paragraph: "backup_retention" },
      { paragraph: "backup_use" },
      { paragraph: "temporary" },
      {
        paragraph: "exceptions",
        children: ["law", "fraud", "security", "complaints", "claims"],
      },
      { paragraph: "claims_period" },
    ],
  },
  {
    key: "rights",
    blocks: [
      {
        paragraph: "p1",
        children: [
          "information",
          "access",
          "rectification",
          "erasure",
          "restriction",
          "objection",
          "portability",
          "withdraw",
        ],
      },
      { paragraph: "p2" },
      { paragraph: "p3" },
      { paragraph: "p4" },
      { paragraph: "p5" },
    ],
  },
  {
    key: "minors_other_people",
    blocks: [
      { paragraph: "p1" },
      { paragraph: "p2" },
      { paragraph: "p3" },
      {
        paragraph: "p4",
        children: ["phone", "email", "address", "documents", "photos", "other"],
      },
    ],
  },
  {
    key: "security_third_parties",
    blocks: [
      { paragraph: "p1" },
      {
        paragraph: "p2",
        children: [
          "access",
          "passwords",
          "https",
          "server",
          "nginx",
          "containers",
          "admin",
        ],
      },
      { paragraph: "p3" },
      { paragraph: "p4" },
      { paragraph: "p5" },
    ],
  },
  {
    key: "changes_languages_contact",
    blocks: [
      { paragraph: "p1" },
      { paragraph: "p2" },
      { paragraph: "p3" },
      { paragraph: "p4" },
      { paragraph: "p5" },
      { paragraph: "p6" },
    ],
  },
] as const;

const { canonicalUrl, currentLocale } = useLocalizedSeoLinks("/privacy-policy");

useSeoMeta({
  title: () => t("public_pages.privacy.seo.title"),
  description: () => t("public_pages.privacy.seo.description"),
  robots: "index, follow",
  ogTitle: () => t("public_pages.privacy.seo.title"),
  ogDescription: () => t("public_pages.privacy.seo.description"),
  ogType: "website",
  ogUrl: () => canonicalUrl.value,
  ogLocale: () => currentLocale.value.ogLocale,
  ogSiteName: "Senta.lt",
});
</script>

<template>
  <PublicPageLayout
    :eyebrow="$t('public_pages.common.legal')"
    :title="$t('public_pages.privacy.title')"
    :description="$t('public_pages.privacy.description')"
  >
    <div class="mb-14 w-full border-y border-app-border py-8 sm:py-9">
      <div class="grid gap-7 sm:grid-cols-2 sm:gap-10">
        <div>
          <p class="text-xs font-bold uppercase tracking-[0.12em] text-primary">
            {{ $t("public_pages.privacy.meta.controller") }}
          </p>
          <p class="mt-2 font-bold text-app-text">
            {{ operatorName }}
          </p>
          <p class="mt-1 text-sm text-app-muted">
            {{ $t("public_pages.privacy.meta.individual") }}
          </p>
          <p v-if="correspondenceAddress" class="mt-1 text-sm text-app-muted">
            {{ $t("public_pages.privacy.meta.address") }}:
            {{ correspondenceAddress }}
          </p>
          <a
            :href="`mailto:${operatorEmail}`"
            class="mt-1 inline-block text-sm font-medium text-primary animated-underline"
          >
            {{ operatorEmail }}
          </a>
        </div>

        <div>
          <p class="text-xs font-bold uppercase tracking-[0.12em] text-primary">
            {{ $t("public_pages.privacy.meta.updated") }}
          </p>
          <p class="mt-2 font-bold text-app-text">
            {{ updatedAt }}
          </p>
          <p class="mt-1 text-sm leading-6 text-app-muted">
            {{ $t("public_pages.privacy.meta.language_note") }}
          </p>
        </div>
      </div>
    </div>

    <div class="space-y-14 sm:space-y-16">
      <section
        v-for="(section, sectionIndex) in sections"
        :key="section.key"
        class="w-full"
      >
        <h2 class="text-xl font-bold tracking-tight text-app-text sm:text-2xl">
          <span class="mr-2 text-primary"> {{ sectionIndex + 1 }}. </span>
          {{ $t(`public_pages.privacy.sections.${section.key}.title`) }}
        </h2>

        <div
          class="mt-5 space-y-4 text-[15px] leading-7 text-app-muted sm:text-base"
        >
          <div
            v-for="(block, blockIndex) in section.blocks"
            :key="`${section.key}-${blockIndex}`"
            class="space-y-3"
          >
            <p class="pl-12 -indent-12">
              <span
                class="inline-block w-12 indent-0 font-semibold tabular-nums text-primary"
              >
                {{ sectionIndex + 1 }}.{{ blockIndex + 1 }}.
              </span>
              {{
                $t(
                  `public_pages.privacy.sections.${section.key}.${block.paragraph}`,
                )
              }}
            </p>

            <div
              v-if="'children' in block && block.children?.length"
              class="space-y-2 pl-5 sm:pl-8"
            >
              <p
                v-for="(child, childIndex) in block.children"
                :key="child"
                class="pl-16 -indent-16"
              >
                <span
                  class="inline-block w-16 indent-0 font-semibold tabular-nums text-primary"
                >
                  {{ sectionIndex + 1 }}.{{ blockIndex + 1 }}.{{
                    childIndex + 1
                  }}.
                </span>
                {{
                  $t(
                    `public_pages.privacy.sections.${section.key}.bullets.${child}`,
                  )
                }}
              </p>
            </div>
          </div>

          <NuxtLink
            v-if="'cookiesLink' in section && section.cookiesLink"
            :to="localeRoute('/cookies') || '/cookies'"
            class="inline-flex font-bold text-primary animated-underline"
          >
            {{ $t("public_pages.privacy.cookies_link") }}
          </NuxtLink>
        </div>
      </section>
    </div>
  </PublicPageLayout>
</template>
